// Реестр CSS custom properties: превращает структурные (scale) и цветовые (palette) токены в
// плоский словарь `--имя → значение` для конкретной resolved-темы. ThemeProvider присваивает этот
// словарь корню (style + data-theme). Компоненты ссылаются на токены только через cssVar('name').
//
// Здесь же — детерминированный `senderColorKey` (R2-бэклог G: владелец алгоритма = design-system):
// стабильный индекс цвета автора по его userId, чтобы reply-полоса / имя в группе / Avatar-fallback
// имели один и тот же цвет в любом месте UI и переживали перерендеры.
import { scaleTokens, cssVarNameOf } from './scale.js';
import { palettes, lightPalette } from './palette.js';
/** Имя CSS-переменной по логическому имени токена (любой scale- или color-токен). */
export function cssVarName(token) {
    return cssVarNameOf(token);
}
/** `var(--font-md)` для inline-стилей/CSS-строк. Второй аргумент — fallback. */
export function cssVar(token, fallback) {
    const name = cssVarNameOf(token);
    return fallback === undefined ? `var(${name})` : `var(${name}, ${fallback})`;
}
/** Плоский словарь структурных токенов (тема-независимых). */
export function scaleCssVars() {
    const out = {};
    for (const [token, value] of Object.entries(scaleTokens)) {
        out[cssVarNameOf(token)] = value;
    }
    return out;
}
/**
 * Плоский словарь цветовых токенов для resolved-темы. Массив `senderColors` разворачивается в
 * индексированные переменные `--color-sender-0..N` + `--color-sender-count` (число вариантов) —
 * так компонент берёт цвет автора как `var(--color-sender-<idx>)`.
 */
export function colorCssVars(theme) {
    const palette = palettes[theme];
    const out = {};
    for (const [token, value] of Object.entries(palette)) {
        if (token === 'senderColors')
            continue;
        out[cssVarNameOf(token)] = value;
    }
    palette.senderColors.forEach((color, i) => {
        out[`--color-sender-${i}`] = color;
    });
    out['--color-sender-count'] = String(palette.senderColors.length);
    return out;
}
/** Полный набор CSS-vars для темы (структурные + цветовые) — то, что вешает ThemeProvider. */
export function themeCssVars(theme) {
    return { ...scaleCssVars(), ...colorCssVars(theme) };
}
/** Сколько различимых цветов автора в палитре (одинаково для обеих тем по построению). */
export const SENDER_COLOR_COUNT = lightPalette.senderColors.length;
/**
 * Детерминированный индекс цвета автора 0..SENDER_COLOR_COUNT-1 по его userId (R2-G).
 * FNV-1a-хэш строки → остаток по числу цветов. Чистая функция: один userId всегда даёт один индекс,
 * независимо от чата/сессии/рендера — это и есть «стабильный цвет автора» из Telegram-паттерна.
 */
export function senderColorIndex(userId) {
    const s = String(userId);
    let h = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        // FNV prime, держим в 32-битном беззнаковом диапазоне через >>> 0
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h % SENDER_COLOR_COUNT;
}
/** CSS-значение цвета автора: `var(--color-sender-<idx>)` по userId (R2-G). */
export function senderColorKey(userId) {
    return `var(--color-sender-${senderColorIndex(userId)})`;
}
//# sourceMappingURL=vars.js.map